import Clarity from '@microsoft/clarity'

import { IMOCKUP_CLARITY_PROJECT_ID } from '@/config/imockupBuildFlags'

/**
 * 当前环境是否允许调用 Clarity SDK。
 *
 * @returns 是否启用埋点
 */
function canTrack(): boolean {
  return import.meta.env.PROD && IMOCKUP_CLARITY_PROJECT_ID.length > 0
}

/**
 * 初始化 Clarity（编辑器）。
 * 仅在生产环境且存在项目 ID 时执行，确保与营销站同一项目配置。
 */
export function initClarityAnalytics(): void {
  if (!canTrack()) return
  Clarity.init(IMOCKUP_CLARITY_PROJECT_ID)
}

/**
 * 上报自定义事件（例如按钮点击、关键转化动作）。
 *
 * @param eventName - 事件名称，建议使用稳定、可枚举命名
 */
function trackEvent(eventName: string): void {
  if (!canTrack()) return
  if (!eventName.trim()) return
  Clarity.event(eventName)
}

/**
 * 设置会话自定义标签，用于筛选与分析分组。
 *
 * @param key - 标签键
 * @param value - 标签值，支持单值或数组
 */
function setSessionTag(key: string, value: string | string[]): void {
  if (!canTrack()) return
  if (!key.trim()) return
  Clarity.setTag(key, value)
}

/**
 * 绑定用户身份信息（custom-id 为必填）。
 *
 * @param customId - 业务内用户唯一标识
 * @param customSessionId - 自定义会话标识
 * @param customPageId - 自定义页面标识
 * @param friendlyName - 友好展示名
 */
function identifyUser(
  customId: string,
  customSessionId?: string,
  customPageId?: string,
  friendlyName?: string,
): void {
  if (!canTrack()) return
  if (!customId.trim()) return
  Clarity.identify(customId, customSessionId, customPageId, friendlyName)
}

/**
 * Clarity 统计封装：统一事件与标签上报入口，便于营销站与编辑器共用命名规范。
 */
export function useClarityAnalytics() {
  return {
    trackEvent,
    setSessionTag,
    identifyUser,
  }
}
